import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";
import { ShieldCheck, Scale, Target } from "lucide-react";

const PRINCIPLES = [
  {
    icon: ShieldCheck,
    title: "Verification-first",
    desc: "We don't just ship software. We prove that what we ship behaves correctly, continuously.",
  },
  {
    icon: Scale,
    title: "Accountability by design",
    desc: "When we're the partner, we're responsible for the outcome. No hand-offs that hide regressions.",
  },
  {
    icon: Target,
    title: "Engineering rigor",
    desc: "Senior engineers from institutions like Goldman Sachs. We treat every system like it processes real money.",
  },
];

export default function About() {
  return (
    <>
      <SEO
        title="About Us | Verification-First Engineering"
        description="United Techlab exists to bring engineering rigor back to enterprise technology: verification-first modernization, accountable delivery, and Goldman-Sachs-grade rigor."
        path="/about"
      />
      <PageHeader
        eyebrow="> /about"
        title="We exist because legacy modernization and AI needs adults in the room."
        subtitle="United Techlab is a verification-first engineering firm. We modernize legacy, ship production software, and build AI systems that actually hold up under enterprise scrutiny."
      />

      <section className="border-b border-white/10 bg-carbon">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="chip text-neon-green">// mission</div>
            <h2 className="mt-3 font-display font-black text-3xl md:text-4xl text-white leading-tight">
              Bring engineering rigor back to enterprise technology.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-white/75 leading-relaxed">
            <p>
              Across enterprises, the same pattern repeats: modernization projects
              fail not because of poor engineering, but because behavior is lost
              in translation. Vendors promise automated conversion; nobody takes
              responsibility for correctness. Modern AI tools are powerful, and
              on their own, dangerous.
            </p>
            <p>
              We built United Techlab as the accountable partner enterprises
              actually need. We start with verification. We take ownership of
              outcomes. We work alongside, or replace, vendors who won't.
            </p>
            <p>
              From there, we expanded: custom software, cloud engineering, AI
              observability, staff augmentation. The thread is the same:{" "}
              <span className="text-neon-green">engineering rigor you can audit</span>.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-obsidian">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="chip text-neon-cyan">&gt; /principles</div>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-5xl text-white leading-tight max-w-2xl">
            What we believe, and how we work.
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-white/10">
            {PRINCIPLES.map((p) => (
              <div
                key={p.title}
                className="border-r border-b border-white/10 p-8 bg-carbon hover:bg-[#111] transition-colors"
              >
                <div className="h-10 w-10 border border-neon-green/40 text-neon-green inline-flex items-center justify-center">
                  <p.icon size={18} />
                </div>
                <h3 className="mt-5 font-display font-bold text-white text-xl">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Meet the team behind the work."
        subtitle="Small, senior, opinionated. Start with the founder."
        ctaLabel="Meet the founder"
        ctaTo="/founder"
        secondaryLabel="See services"
        secondaryTo="/services"
      />
    </>
  );
}
